import FeatureGating from './FeatureGating';
import { FlowModeEnum } from '@/enum/flow_mode';
import { LocalStorageHelper } from '@/services/LocalStorageHelper';
import StalePromotionRegistry from '@/services/StalePromotionRegistry';
import { snapchat } from '@/gen/proto/protos';
import { captureMessage } from '@sentry/vue';
import AvatarBuilderPromoData = snapchat.bitmoji.api.AvatarBuilderPromoData;
import INewCategoriesPromoItem = snapchat.bitmoji.api.INewCategoriesPromoItem;
import INewOptionsPromoItem = snapchat.bitmoji.api.INewOptionsPromoItem;
import INewSectionsPromoItem = snapchat.bitmoji.api.INewSectionsPromoItem;

const PROMOS_PROTO_PATH = './promotions/avatar_builder_promotions.proto';

/**
 * Fetches active promos from s3, reconciles them against already viewed items, returns promoted items not yet viewed.
 */
const loadPromotions = (flowMode: FlowModeEnum): Promise<BuilderPromotions> => {
  const localStorage = new LocalStorageHelper();
  return new Promise<void>((resolve, reject) => {
    if (!checkPromosEnabled(flowMode, localStorage)) {
      reject();
    }
    resolve();
  })
    .then(() => fetch(PROMOS_PROTO_PATH))
    .then((response: Response) => {
      if (response.ok) {
        return response.arrayBuffer();
      }
      if (response.status !== 404)
        captureMessage(`Failed to fetch promotions. Unhealthy response: ${response}`);
      return Promise.reject();
    })
    .then((responseBuffer: ArrayBuffer) =>
      AvatarBuilderPromoData.decode(new Uint8Array(responseBuffer)),
    )
    .then((promotions) => parsePromotions(promotions, localStorage))
    .catch((err) => {
      if (err) captureMessage(`Failed to parse promotions. Error: ${err}`);
      return { categories: new Set(), options: [], sections: [] };
    });
};

const flushPromotions = (stalePromotions: BuilderPromotions) => {
  if (stalePromotions.options.length === 0) {
    // Nothing to flush
    return;
  }

  const localStorageHelper = new LocalStorageHelper();
  if (!localStorageHelper.isStorageAvailable()) {
    // Very unexpected
    captureMessage(
      'Failed to store stale promotion records - local storage unexpectedly unavailable!',
    );
    return;
  }
  const stalePromoRegistry = new StalePromotionRegistry(localStorageHelper);
  stalePromoRegistry.setStalePromotions(stalePromotions.options);
};

const checkPromosEnabled = (flowMode: FlowModeEnum, localStorage: LocalStorageHelper): boolean => {
  return (
    flowMode !== FlowModeEnum.CREATE &&
    localStorage.isStorageAvailable() &&
    FeatureGating.arePromosEnabled()
  );
};

const parsePromotions = (
  promotions: AvatarBuilderPromoData,
  localStorage: LocalStorageHelper,
): BuilderPromotions => {
  const decoder = new TextDecoder('utf-8');
  const stalePromoRegistry = new StalePromotionRegistry(localStorage);
  const currentEpochSeconds = new Date().getTime() / 1000;
  const derivedCategoryPromos = new Set();

  // Ignore option promotions previously viewed
  const allCurrentOptionPromos: OptionPromotions[] = [];
  const optionPromos = promotions.new_option_ids
    .map((promo: INewOptionsPromoItem) => {
      return {
        category: parseCategory(decoder, promo.category),
        optionIds: validateRequiredField('optionIds', promo.option_ids),
      };
    })
    .map((promo: OptionPromotions) => {
      allCurrentOptionPromos.push(promo);
      // Exclude stale options
      const filteredOptionIds =
        promo.optionIds?.filter(
          (id) => !stalePromoRegistry.isOptionPromotionStale(promo.category, id),
        ) || [];
      if (filteredOptionIds.length > 0) {
        derivedCategoryPromos.add(promo.category);
      }
      return {
        category: promo.category,
        optionIds: filteredOptionIds,
      };
    });

  // Housekeeping to prevent registry from growing indefinitely
  stalePromoRegistry.cleanUpUnusedRecords(allCurrentOptionPromos);

  // Ignore expired section promotions
  const sectionPromos = promotions.new_sections
    .filter((promo: INewSectionsPromoItem) =>
      isTimedPromoLive(currentEpochSeconds, promo.starts, promo.expires),
    )
    .map((promo: INewSectionsPromoItem) => {
      return {
        category: parseCategory(decoder, promo.category),
        sectionId: validateRequiredField('sectionId', promo.section_id),
      };
    });

  // Ignore expired category promos or untimed (i.e. unforced) category promos that aren't derived
  // from option promotions
  const categoryPromos = new Set(
    promotions.new_categories
      .filter((promo: INewCategoriesPromoItem) =>
        isTimedPromoLive(currentEpochSeconds, promo.starts, promo.expires),
      )
      .filter(
        (promo: INewCategoriesPromoItem) =>
          promo.starts ||
          promo.expires ||
          derivedCategoryPromos.has(parseCategory(decoder, promo.category)),
      )
      .map((promo: INewCategoriesPromoItem) => parseCategory(decoder, promo.category)),
  );

  return {
    categories: categoryPromos,
    sections: sectionPromos,
    options: optionPromos,
  };
};

const parseCategory = (decoder: TextDecoder, category: Uint8Array | null | undefined): string => {
  const parsedCategory = decoder.decode(validateRequiredField('category', category));

  // Convert tooling/assets key for outfit category to builder key
  return parsedCategory === 'outfits' ? 'outfit' : parsedCategory;
};

const isTimedPromoLive = (
  currentEpochSeconds: number,
  starts: number,
  expires: number,
): boolean => {
  return (
    (starts || currentEpochSeconds) <= currentEpochSeconds &&
    currentEpochSeconds <= (expires || currentEpochSeconds)
  );
};

const validateRequiredField = (fieldName: string, field: any | null | undefined): any => {
  if (!field) {
    throw new Error(`Missing required promo field: '${fieldName}`);
  }
  return field;
};

const isNestedPromoForCategory = (
  category: String,
  nestedPromotion: SectionPromotion | OptionPromotions,
): Boolean => {
  return nestedPromotion.category === category;
};

export default {
  loadPromotions,
  flushPromotions,
  isNestedPromoForCategory,
};
